import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, CheckCircle2, XCircle, MessageSquarePlus, Loader2,
  Inbox, Clock, ListChecks, RefreshCw, ChevronRight, ChevronDown,
  AlertTriangle,
} from "lucide-react";
import {
  goCreateTask, goListTasks, goTaskAction, goPacks,
} from "@/lib/api";
import { toast } from "sonner";

/** Client Command Center — spec §9. Answers 3 questions on landing:
 *  1) What did WebsiteTech complete? 2) What results did it produce?
 *  3) What needs my approval? Phase 1: task + approvals surface. */

const STATUS_COLOR = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  awaiting_approval: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  changes_requested: "bg-sky-100 text-sky-800 border-sky-200",
  rejected: "bg-rose-100 text-rose-700 border-rose-200",
};
const STATUS_LABEL = {
  draft: "Draft", awaiting_approval: "Awaiting approval",
  approved: "Approved", changes_requested: "Changes requested",
  rejected: "Rejected",
};

const AGENT_TONE = {
  content: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  website: "bg-sky-50 text-sky-700 border-sky-200",
  leads: "bg-orange-50 text-orange-700 border-orange-200",
  reputation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  analytics: "bg-slate-50 text-slate-700 border-slate-200",
  human: "bg-violet-50 text-violet-700 border-violet-200",
};

const EXAMPLES = [
  "Create four social posts about our new catering menu.",
  "Build a landing page for dental implants.",
  "Follow up with leads who haven't replied in three days.",
  "Send me a drafted response to every new review.",
  "Prepare a customer-satisfaction survey.",
  "Promote our slow Tuesday appointments.",
];

const draftPreview = (md) => {
  if (!md) return "";
  return md.slice(0, 240) + (md.length > 240 ? "…" : "");
};

const TaskCard = ({ t, onAction, onExpand, expanded }) => {
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(t.draft_output || "");
  const [saving, setSaving] = useState(false);

  const run = async (action, extra = {}) => {
    setSaving(true);
    try {
      await onAction(t.id, { action, note: note || undefined, ...extra });
      setNote(""); setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200 p-5" data-testid={`hub-task-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={STATUS_COLOR[t.status] || STATUS_COLOR.draft}>{STATUS_LABEL[t.status] || t.status}</Badge>
            <Badge variant="outline" className={AGENT_TONE[t.agent] || ""}>{t.agent_name}</Badge>
            {t.priority === "urgent" && <Badge className="bg-rose-100 text-rose-800 border-rose-200">Urgent</Badge>}
          </div>
          <div className="mt-2 font-heading text-base font-bold text-slate-900">{t.title}</div>
          <div className="mt-1 text-xs text-slate-500">Requested: {new Date(t.created_at).toLocaleString()}</div>
          <div className="mt-2 text-xs text-slate-600 italic">"{t.prompt}"</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onExpand(expanded ? null : t.id)} data-testid={`hub-task-toggle-${t.id}`}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {expanded ? "Hide" : "Review"}
        </Button>
      </div>

      {!expanded && t.draft_output && (
        <div className="mt-3 line-clamp-3 rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
          {draftPreview(t.draft_output)}
        </div>
      )}

      {expanded && (
        <div className="mt-4 space-y-3" data-testid={`hub-task-details-${t.id}`}>
          {t.missing_info?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="flex items-center gap-1 font-bold"><AlertTriangle className="h-3.5 w-3.5" /> Missing info</div>
              <ul className="mt-1 list-disc pl-5">{t.missing_info.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-xs uppercase tracking-wider text-slate-500">
              Draft output
              {t.status === "awaiting_approval" && (
                <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)} data-testid={`hub-edit-toggle-${t.id}`}>
                  {editing ? "Cancel edit" : "Edit draft"}
                </Button>
              )}
            </div>
            {editing ? (
              <>
                <Textarea
                  data-testid={`hub-edit-draft-${t.id}`}
                  className="min-h-[180px] rounded-none border-0"
                  value={editVal} onChange={(e) => setEditVal(e.target.value)}
                />
                <div className="flex justify-end gap-2 p-2">
                  <Button size="sm" onClick={() => run("edit_draft", { edited_draft: editVal })} disabled={saving} data-testid={`hub-edit-save-${t.id}`}>
                    Save draft
                  </Button>
                </div>
              </>
            ) : (
              <div className="whitespace-pre-wrap p-3 text-sm text-slate-800">{t.draft_output || <span className="italic text-slate-400">No draft yet.</span>}</div>
            )}
          </div>

          {t.risks?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              <div className="font-bold">Risks</div>
              <ul className="mt-1 list-disc pl-5">{t.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}

          {t.status === "awaiting_approval" && (
            <div className="space-y-2">
              <Textarea
                data-testid={`hub-note-${t.id}`}
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note (optional)"
                className="min-h-[60px]"
              />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => run("approve")} disabled={saving} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`hub-approve-${t.id}`}>
                  <CheckCircle2 className="mr-1 h-4 w-4" /> Approve & publish
                </Button>
                <Button size="sm" variant="outline" onClick={() => run("request_changes")} disabled={saving} data-testid={`hub-changes-${t.id}`}>
                  <MessageSquarePlus className="mr-1 h-4 w-4" /> Request changes
                </Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => run("reject")} disabled={saving} data-testid={`hub-reject-${t.id}`}>
                  <XCircle className="mr-1 h-4 w-4" /> Reject
                </Button>
              </div>
            </div>
          )}

          {t.history?.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">History</div>
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                {t.history.map((h, i) => (
                  <li key={i}>{new Date(h.at).toLocaleString()} · <b>{h.event}</b>{h.by && ` — ${h.by}`}{h.detail && ` · ${h.detail}`}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default function GrowthOperatorHub() {
  const [packs, setPacks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("");
  const [prompt, setPrompt] = useState("");
  const [pack, setPack] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await goListTasks(filter || undefined);
      setTasks(r.tasks || []); setCounts(r.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign in required to view the Command Center.");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    goPacks().then(r => setPacks(r.packs || []));
    load();
  }, [load]);

  const submit = async () => {
    if (!prompt.trim()) return;
    setCreating(true);
    try {
      await goCreateTask({ prompt: prompt.trim(), industry_pack: pack || undefined });
      setPrompt("");
      toast.success("Request received — draft ready for review");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create task");
    } finally { setCreating(false); }
  };

  const action = async (tid, payload) => {
    try {
      await goTaskAction(tid, payload);
      toast.success("Updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const awaiting = counts.awaiting_approval || 0;
  const approved = counts.approved || 0;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hub-page">
      <Navbar />

      {/* Today panel */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="text-xs font-semibold uppercase tracking-widest text-orange-600">Command Center</div>
          <h1 className="mt-2 font-heading text-3xl font-black text-slate-900 sm:text-4xl">Today</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            What did WebsiteTech do, what did it produce, what needs your approval — one place.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Card className="border-amber-200 bg-amber-50 p-5" data-testid="hub-stat-awaiting">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
                <Inbox className="h-4 w-4" /> Awaiting your approval
              </div>
              <div className="mt-2 font-heading text-4xl font-black tabular-nums text-amber-900">{awaiting}</div>
              <div className="text-xs text-amber-800/80">Drafts drafted, waiting on you.</div>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50 p-5" data-testid="hub-stat-approved">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-800">
                <CheckCircle2 className="h-4 w-4" /> Approved & published
              </div>
              <div className="mt-2 font-heading text-4xl font-black tabular-nums text-emerald-900">{approved}</div>
              <div className="text-xs text-emerald-800/80">Completed and released to your channels.</div>
            </Card>
            <Card className="border-slate-200 bg-white p-5" data-testid="hub-stat-total">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <ListChecks className="h-4 w-4" /> Total requests
              </div>
              <div className="mt-2 font-heading text-4xl font-black tabular-nums text-slate-900">{total}</div>
              <div className="text-xs text-slate-500">Across every specialist agent.</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Task submit */}
      <section className="mx-auto max-w-6xl px-4 py-8" data-testid="hub-submit">
        <Card className="border-slate-200 p-6">
          <div className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <Sparkles className="h-5 w-5 text-orange-500" /> Ask your Growth Operator
          </div>
          <div className="mt-1 text-sm text-slate-500">
            Describe the outcome in plain English. We'll pick the right agent, draft it, and wait for your approval.
          </div>
          <Textarea
            data-testid="hub-prompt"
            value={prompt} onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. Promote our slow Tuesday appointments this week."
            className="mt-4 min-h-[110px]"
          />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <select
              data-testid="hub-pack"
              value={pack}
              onChange={(e) => setPack(e.target.value)}
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">General (no industry pack)</option>
              {packs.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
            </select>
            <Button onClick={submit} disabled={creating || !prompt.trim()} className="bg-orange-500 text-white hover:bg-orange-400" data-testid="hub-submit-btn">
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Ask the Operator
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider">Try:</span>
            {EXAMPLES.map((e, i) => (
              <button
                key={i} onClick={() => setPrompt(e)}
                className="rounded-full border border-slate-200 px-2.5 py-1 text-slate-700 hover:border-orange-400 hover:text-orange-600"
                data-testid={`hub-example-${i}`}
              >
                {e}
              </button>
            ))}
          </div>
        </Card>
      </section>

      {/* Work Center */}
      <section className="mx-auto max-w-6xl px-4 py-6" data-testid="hub-work-center">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Work Center</h2>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={filter === "" ? "default" : "outline"} onClick={() => setFilter("")} data-testid="hub-filter-all">
              All <span className="ml-1 opacity-60">({total})</span>
            </Button>
            {["awaiting_approval", "approved", "changes_requested", "rejected"].map(s => (
              <Button
                key={s} size="sm"
                variant={filter === s ? "default" : "outline"}
                onClick={() => setFilter(s)}
                data-testid={`hub-filter-${s}`}
              >
                {STATUS_LABEL[s]} <span className="ml-1 opacity-60">({counts[s] || 0})</span>
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={load} data-testid="hub-refresh">
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
            </Button>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && tasks.length === 0 && (
            <Card className="border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="hub-empty">
              No tasks yet. Ask your Growth Operator above.
            </Card>
          )}
          {tasks.map(t => (
            <TaskCard key={t.id} t={t} onAction={action} onExpand={setExpanded} expanded={expanded === t.id} />
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
